import {
  useContext,
  useEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import RefreshIcon from '@mui/icons-material/Refresh';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useGetOrderRemindersQuery } from '#services/order';
import { formatDate } from '#common/utils';
import { FeedbackUIContext } from '#context/feedback-ui-context';

import {
  ReminderChip,
  getReminderMeta,
  getDefaultReminderRange,
  normalizeStartOfDay,
  normalizeEndOfDay,
} from './reminder.utils';

interface DateRange {
  from: Date | null;
  to: Date | null;
}

interface OverdueRow {
  id?: string | number;
  createdAsUserName?: string;
  createdDate?: string;
  nextEstimatedShipDate?: string;
  [key: string]: unknown;
}

const DEFAULT_REMINDER_PAGE_LIMIT = 25;

const FILTER_OPTIONS = [{
  value: 'orderId',
  label: 'Order #',
}, {
  value: 'poNumber',
  label: 'PO #',
}, {
  value: 'style',
  label: 'Style #',
}, {
  value: 'inventoryOrderId',
  label: 'Inventory #',
}, {
  value: 'storeName',
  label: 'Store Name',
}];

const normalizeRange = (
  key: 'from' | 'to',
  value: Date | null,
  context: { validationError?: unknown } | undefined,
  updater: Dispatch<SetStateAction<DateRange>>,
  timeZone?: string,
) => {
  if (value && context?.validationError) {
    return;
  }
  updater((prev) => {
    let nextValue = value instanceof Date && !Number.isNaN(value.getTime()) ? value : null;
    if (!nextValue && value === null) {
      nextValue = null;
    }
    if (nextValue) {
      nextValue = (key === 'from')
        ? normalizeStartOfDay(nextValue, timeZone)
        : normalizeEndOfDay(nextValue, timeZone);
    }
    const updated: DateRange = { ...prev, [key]: nextValue };
    if (updated.from && updated.to && updated.from > updated.to) {
      if (key === 'from') {
        updated.to = nextValue;
      } else {
        updated.from = nextValue;
      }
    }
    return updated;
  });
};

export default function OverdueOrders() {
  const navigate = useNavigate();
  const appConfig = useSelector(
    (state: { appConfig: { shippingReminderThresholdDays?: number | string; timeZone?: string } }) => state.appConfig,
  );
  const shippingReminderThresholdDays = Number(appConfig?.shippingReminderThresholdDays) || 10;
  const marketTimeZone = appConfig?.timeZone;
  const { showBackdrop, hideBackdrop } = useContext(FeedbackUIContext)!;
  const overlayActiveRef = useRef(false);

  const [filterKey, setFilterKey] = useState('orderId');
  const [filterValue, setFilterValue] = useState('');
  const [debouncedFilter] = useDebounce(filterValue, 300);
  const [reminderDateRange, setReminderDateRange] = useState<DateRange>(
    () => getDefaultReminderRange(marketTimeZone),
  );
  const [showAllReminders, setShowAllReminders] = useState(false);
  const [excludeReceived, setExcludeReceived] = useState(true);

  const reminderDateFromIso = reminderDateRange.from ? reminderDateRange.from.toISOString() : undefined;
  const reminderDateToIso = reminderDateRange.to ? reminderDateRange.to.toISOString() : undefined;
  const reminderQueryLimit = showAllReminders ? 0 : DEFAULT_REMINDER_PAGE_LIMIT;

  const reminderQuery = useGetOrderRemindersQuery({
    filter: debouncedFilter,
    filterKey,
    headerFilters: {},
    pageIndex: 0,
    pageSize: reminderQueryLimit,
    shippingReminderLimit: reminderQueryLimit,
    shippingReminderDateFrom: reminderDateFromIso,
    shippingReminderDateTo: reminderDateToIso,
    excludeReceived,
  }, {
    refetchOnMountOrArgChange: true,
  });

  const reminderData = reminderQuery.data as { rows?: OverdueRow[]; total?: number } | undefined;
  const reminderRows = reminderData?.rows || [];
  const reminderTotal = reminderData?.total ?? reminderRows.length;
  const remindersLoading = reminderQuery.isLoading || reminderQuery.isFetching;
  const reminderError = reminderQuery.error;
  const canToggleReminderAmount = reminderTotal > DEFAULT_REMINDER_PAGE_LIMIT;

  useEffect(() => {
    if (remindersLoading && !overlayActiveRef.current) {
      showBackdrop();
      overlayActiveRef.current = true;
    } else if (!remindersLoading && overlayActiveRef.current) {
      hideBackdrop();
      overlayActiveRef.current = false;
    }

    return () => {
      if (overlayActiveRef.current) {
        hideBackdrop();
        overlayActiveRef.current = false;
      }
    };
  }, [remindersLoading, showBackdrop, hideBackdrop]);

  const handleReminderDateChange = (
    key: 'from' | 'to',
    value: Date | null,
    context: { validationError?: unknown } | undefined,
  ) => {
    normalizeRange(key, value, context, setReminderDateRange, marketTimeZone);
  };

  const handleResetFilters = () => {
    setFilterKey('orderId');
    setFilterValue('');
    setReminderDateRange(getDefaultReminderRange(marketTimeZone));
    setExcludeReceived(true);
  };

  const buildOrderDetailUrl = (orderId?: string | number) => `/management/order/detail?orderId=${orderId}`;

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h5" gutterBottom>
            Customer Orders
          </Typography>
          <Typography variant="h6">
            Overdue Orders
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Focus on orders whose earliest ship date is overdue or within
            {' '}
            {shippingReminderThresholdDays}
            {' '}
            days.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          onClick={() => { void navigate('/management/order/list'); }}
          sx={{ mt: { xs: 1, md: 0 } }}
        >
          Back to Customer Orders
        </Button>
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={2}
          sx={{
            alignItems: { xs: 'stretch', lg: 'center' },
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
              flex: 1,
            }}
          >
            <TextField
              select
              size="small"
              label="Filter by"
              value={filterKey}
              onChange={(e) => setFilterKey(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label="Keyword"
              fullWidth
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
            />
          </Stack>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <DatePicker
                label="ETA from"
                value={reminderDateRange.from}
                onChange={(date, ctx) => handleReminderDateChange('from', date, ctx)}
                slotProps={{ textField: { size: 'small' } }}
              />
              <DatePicker
                label="ETA to"
                value={reminderDateRange.to}
                onChange={(date, ctx) => handleReminderDateChange('to', date, ctx)}
                slotProps={{ textField: { size: 'small' } }}
              />
            </Stack>
          </LocalizationProvider>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{
              alignItems: 'center',
              mt: '-8px !important',
            }}
          >
            <FormControlLabel
              control={(
                <Checkbox
                  checked={excludeReceived}
                  onChange={(e) => setExcludeReceived(e.target.checked)}
                  color="primary"
                />
              )}
              label="Exclude received"
            />
            <Button
              variant="outlined"
              size="small"
              sx={{ height: 40 }}
              onClick={handleResetFilters}
            >
              Reset
            </Button>
            <Tooltip title="Refresh reminders">
              <span>
                <IconButton
                  color="primary"
                  onClick={() => { void reminderQuery.refetch(); }}
                  disabled={remindersLoading}
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {reminderTotal}
              {' '}
              overdue or due-soon orders
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              Threshold configured at
              {' '}
              {shippingReminderThresholdDays}
              {' '}
              days. Use the filters above to narrow the list.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            {canToggleReminderAmount && (
              <Button
                size="small"
                onClick={() => setShowAllReminders((prev) => !prev)}
              >
                {showAllReminders
                  ? `Show first ${DEFAULT_REMINDER_PAGE_LIMIT}`
                  : `Show all ${reminderTotal}`}
              </Button>
            )}
          </Stack>
        </Stack>
        <Divider sx={{ mb: 2 }} />
        {remindersLoading ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            Loading reminders...
          </Typography>
        ) : reminderError ? (
          <Typography variant="body2" color="error">
            Unable to load reminders. Please try again later.
          </Typography>
        ) : reminderRows.length ? (
          <Stack spacing={1.5}>
            {reminderRows.map((row) => {
              const reminderMeta = getReminderMeta(row);
              const detailUrl = buildOrderDetailUrl(row.id);
              return (
                <Box
                  key={row.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 1.5,
                    backgroundColor: reminderMeta?.backgroundColor || 'transparent',
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1}
                    sx={{
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', md: 'flex-start' },
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Order #
                        {row.id}
                        {' '}
                        ·
                        {' '}
                        {row.createdAsUserName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        Created:
                        {' '}
                        {row.createdDate ? formatDate(row.createdDate) : '—'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        ETA:
                        {' '}
                        {row.nextEstimatedShipDate ? formatDate(row.nextEstimatedShipDate) : '—'}
                      </Typography>
                    </Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{
                        alignItems: 'center',
                      }}
                    >
                      <ReminderChip row={row} />
                      <Link
                        href={detailUrl}
                        underline="hover"
                        variant="body2"
                        onClick={(e) => {
                          e.preventDefault();
                          void navigate(detailUrl);
                        }}
                      >
                        View
                      </Link>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
            {!showAllReminders && canToggleReminderAmount && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                Showing first
                {' '}
                {DEFAULT_REMINDER_PAGE_LIMIT}
                {' '}
                orders.
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            All caught up for the configured reminder window.
          </Typography>
        )}
      </Paper>
    </Box>
  );
}
